<template>
  <Dialog
    :visible="demoDialogVisible"
    modal
    :header="copy.title"
    :style="{ width: '40rem' }"
    :breakpoints="{ '640px': '95vw' }"
    data-testid="demo-request-dialog"
    @update:visible="onVisibleChange"
  >
    <div v-if="submitted" class="text-center py-6" data-testid="demo-request-success">
      <span class="mx-auto mb-4 w-14 h-14 rounded-full bg-green-100 text-green-700 flex items-center justify-center" aria-hidden="true">
        <i class="pi pi-check text-2xl"></i>
      </span>
      <p class="text-lg font-semibold text-gray-900">Request received</p>
      <p class="mt-2 text-gray-600">{{ successMessage }}</p>
      <Button label="Close" class="mt-6" data-testid="demo-request-close" @click="close" />
    </div>

    <form v-else novalidate class="flex flex-col gap-4" data-testid="demo-request-form" @submit.prevent="submit">
      <p class="text-sm text-gray-600 m-0">{{ copy.intro }}</p>

      <div class="grid sm:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label for="demo-name" class="text-sm font-medium text-gray-700">Full name *</label>
          <InputText id="demo-name" v-model="form.name" autocomplete="name" :invalid="!!fieldError('name')" data-testid="demo-name" />
          <small v-if="fieldError('name')" class="p-error" data-testid="demo-name-error">{{ fieldError('name') }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label for="demo-email" class="text-sm font-medium text-gray-700">Work email *</label>
          <InputText id="demo-email" v-model="form.email" type="email" autocomplete="email" :invalid="!!fieldError('email')" data-testid="demo-email" />
          <small v-if="fieldError('email')" class="p-error" data-testid="demo-email-error">{{ fieldError('email') }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label for="demo-institution" class="text-sm font-medium text-gray-700">Institution name *</label>
          <InputText id="demo-institution" v-model="form.institution_name" autocomplete="organization" :invalid="!!fieldError('institution_name')" data-testid="demo-institution" />
          <small v-if="fieldError('institution_name')" class="p-error" data-testid="demo-institution-error">{{ fieldError('institution_name') }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label for="demo-role" class="text-sm font-medium text-gray-700">Your role</label>
          <InputText id="demo-role" v-model="form.role" placeholder="e.g. Principal, Registrar, Bursar" autocomplete="organization-title" :invalid="!!fieldError('role')" data-testid="demo-role" />
          <small v-if="fieldError('role')" class="p-error">{{ fieldError('role') }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label for="demo-country" class="text-sm font-medium text-gray-700">Country</label>
          <Select
            input-id="demo-country"
            :model-value="form.country_code"
            :options="countryOptions"
            option-label="label"
            option-value="value"
            filter
            placeholder="Select your country"
            :invalid="!!fieldError('country_code')"
            data-testid="demo-country"
            @update:model-value="selectCountry"
          />
          <small v-if="fieldError('country_code')" class="p-error">{{ fieldError('country_code') }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label for="demo-phone" class="text-sm font-medium text-gray-700">Phone / WhatsApp</label>
          <InputGroup>
            <InputGroupAddon v-if="dialCode" data-testid="demo-dial-code">{{ dialCode }}</InputGroupAddon>
            <InputText id="demo-phone" v-model="form.phone" type="tel" autocomplete="tel-national" :invalid="!!fieldError('phone')" data-testid="demo-phone" />
          </InputGroup>
          <small v-if="fieldError('phone')" class="p-error" data-testid="demo-phone-error">{{ fieldError('phone') }}</small>
        </div>

        <div class="flex flex-col gap-1">
          <label for="demo-type" class="text-sm font-medium text-gray-700">Institution type</label>
          <Select
            v-model="form.institution_type"
            input-id="demo-type"
            :options="institutionTypeOptions"
            option-label="label"
            option-value="value"
            placeholder="Select type"
            show-clear
            :invalid="!!fieldError('institution_type')"
            data-testid="demo-institution-type"
          />
          <small v-if="fieldError('institution_type')" class="p-error">{{ fieldError('institution_type') }}</small>
        </div>
        <div class="flex flex-col gap-1">
          <label for="demo-students" class="text-sm font-medium text-gray-700">Number of students</label>
          <Select
            v-model="form.student_count"
            input-id="demo-students"
            :options="STUDENT_COUNT_OPTIONS"
            option-label="label"
            option-value="value"
            placeholder="Select range"
            show-clear
            :invalid="!!fieldError('student_count')"
            data-testid="demo-student-count"
          />
          <small v-if="fieldError('student_count')" class="p-error">{{ fieldError('student_count') }}</small>
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label for="demo-message" class="text-sm font-medium text-gray-700">Anything we should know?</label>
        <Textarea id="demo-message" v-model="form.message" rows="3" auto-resize placeholder="Modules you're interested in, current system, timelines…" :invalid="!!fieldError('message')" data-testid="demo-message" />
        <small v-if="fieldError('message')" class="p-error">{{ fieldError('message') }}</small>
      </div>

      <!-- Honeypot: hidden from people and assistive tech; bots fill it. -->
      <div class="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
        <label for="demo-website">Website</label>
        <input id="demo-website" v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
      </div>

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
        <Button type="button" label="Cancel" severity="secondary" text @click="close" />
        <Button type="submit" :label="copy.submit" icon="pi pi-send" :loading="isSubmitting" data-testid="demo-request-submit" />
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useLandingStore } from '@/store/landingStore';
import { useDemoRequestForm } from '@/composables/landing/useDemoRequestForm';
import { useLandingCountry } from '@/composables/landing/useLandingCountry';
import { countryOptions as buildCountryOptions } from '@/utils/landing/countryProfiles';
import { DEMO_DIALOG_COPY, INSTITUTION_TYPE_OPTIONS, STUDENT_COUNT_OPTIONS } from '@/utils/landing/demoRequest';

const store = useLandingStore();
const { demoDialogVisible } = storeToRefs(store);
const { profile } = useLandingCountry();
const { form, demoKind, dialCode, isSubmitting, submitted, successMessage, fieldError, selectCountry, submit, reset } = useDemoRequestForm();

const copy = computed(() => DEMO_DIALOG_COPY[demoKind.value]);
const institutionTypeOptions = computed(() => INSTITUTION_TYPE_OPTIONS(profile.value.schoolLevels));
const countryOptions = computed(() => buildCountryOptions(form.value.country_code));

function close() {
  store.closeDemo();
}

function onVisibleChange(visible: boolean) {
  if (!visible) close();
}

// A fresh form each time the dialog opens after a successful send.
watch(demoDialogVisible, (visible) => {
  if (visible && submitted.value) reset();
});
</script>
